import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ChevronLeft, ChevronRight, ShieldCheck, FlaskConical, Users, Stethoscope,
  Building2, Briefcase, Award, Heart, GraduationCap, FileText, ExternalLink, Bell, Megaphone, Link2,
  Trophy, BookOpen, Activity, Download,
} from "lucide-react";
import { useNursing } from "./NursingLayout";
import { STATIC_CONTENT, slugify, renderInstituteHtml } from "./_shared";
import RecentAcademicsWidget from "@/components/RecentAcademicsWidget";
import AlumniStrip from "@/components/AlumniStrip";

function toYoutubeEmbed(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    let id = "";
    if (host === "youtu.be") id = u.pathname.split("/").filter(Boolean)[0] || "";
    else if (host.endsWith("youtube.com") || host.endsWith("youtube-nocookie.com")) {
      if (u.pathname === "/watch") id = u.searchParams.get("v") || "";
      else if (u.pathname.startsWith("/embed/")) id = u.pathname.split("/")[2] || "";
    }
    return id ? `https://www.youtube.com/embed/${id}?rel=0` : "";
  } catch { return ""; }
}

export default function Home() {
  const data = useNursing();
  const academics = data.academics || [];
  const inst = data.institute || {};
  const departments = (data.departments || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const admissions = data.notices_admission || [];
  const links = data.links || [];
  const notices = data.notices_general || [];
  const slides = data.header_slides || [];
  const gallery = data.gallery || [];
  const ytEmbed = toYoutubeEmbed(inst.youtube_url);
  const yearsExcellence = inst.year_of_establishment
    ? Math.max(1, new Date().getFullYear() - Number(inst.year_of_establishment))
    : 45;

  return (
    <div data-testid="nur-home">
      <Hero slides={slides} inst={inst} />

      {/* Quick stats below hero */}
      <section className="bg-white border-b border-[#E2E8F0]" data-testid="nur-quickstats">
        <div className="max-w-7xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Stat icon={Award} value={`${yearsExcellence}+`} label="Years of Excellence" />
          <Stat icon={Stethoscope} value="100%" label="Clinical Exposure" />
          <Stat icon={Briefcase} value="95%" label="Placement Assistance" />
          <Stat icon={Building2} value="15+" label="Associated Hospitals" />
        </div>
      </section>

      {/* Why choose */}
      <section className="bg-[#F4F9FF] py-12 md:py-16" data-testid="nur-why">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-center font-['Poppins',sans-serif] font-bold text-2xl md:text-4xl text-[#0F2A5F]">
            Why Choose <span className="text-[#0FB28A]">MIT Nursing College?</span>
          </h2>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {[
              { icon: ShieldCheck, l: "INC & State Nursing Council Approved" },
              { icon: FlaskConical, l: "Well Equipped Simulation Labs" },
              { icon: Users, l: "Experienced & Caring Faculty" },
              { icon: Stethoscope, l: "100% Clinical Training" },
              { icon: Building2, l: "Hostel & Transport Facility" },
              { icon: Briefcase, l: "Placement Support" },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.l} className="bg-white rounded-xl p-5 ring-1 ring-[#E2E8F0] hover:ring-[#0FB28A] hover:shadow-md transition-all flex flex-col items-center text-center">
                  <span className="w-12 h-12 rounded-xl bg-[#E0F5EE] text-[#0FB28A] flex items-center justify-center"><Icon size={22} strokeWidth={1.5} /></span>
                  <span className="mt-3 text-xs sm:text-sm font-medium text-[#0F2A5F] leading-snug">{p.l}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Programmes Offered */}
      {departments.length > 0 && (
        <section className="py-12 md:py-20 bg-white" data-testid="nur-programs">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className="font-['Poppins',sans-serif] font-bold text-2xl md:text-4xl text-[#0F2A5F]">Programmes Offered</h2>
              <div className="mt-2 h-1 w-20 bg-[#0FB28A] mx-auto rounded-full" />
            </div>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
              {departments.slice(0, 5).map((d, i) => (
                <Link key={d.id} to={`departments/${slugify(d.name)}`} data-testid={`nur-home-dept-${d.id}`}
                  className="group bg-white rounded-2xl p-5 ring-1 ring-[#E2E8F0] hover:ring-[#0FB28A] hover:shadow-lg hover:-translate-y-1 transition-all flex flex-col items-center text-center">
                  <span className="w-16 h-16 rounded-full bg-[#E0F5EE] text-[#0FB28A] flex items-center justify-center mb-3 group-hover:bg-[#0FB28A] group-hover:text-white transition-colors">
                    <GraduationCap size={28} strokeWidth={1.5} />
                  </span>
                  <h3 className="font-['Poppins',sans-serif] font-bold text-[#0F2A5F] uppercase text-sm leading-tight">{d.name}</h3>
                  {d.course_type && <div className="text-[11px] text-[#475569] mt-1">{d.course_type}</div>}
                  {d.code && <div className="text-[10px] uppercase tracking-wider text-[#0E916F] font-mono mt-1">{d.code}</div>}
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#0FB28A] group-hover:text-[#0E916F]">Know More <ArrowRight size={12} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Feature cards (Clinical / Labs / Placement / Campus) */}
      <section className="py-12 md:py-20 bg-[#F4F9FF]">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Heart, title: "Clinical Training Partners", body: "Hands-on training with renowned hospitals for real-world exposure.", cta: "View Hospitals", to: "facilities" },
            { icon: FlaskConical, title: "Simulation & Skill Labs", body: "Advanced labs to build skills, confidence, and clinical competence.", cta: "Explore Labs", to: "facilities" },
            { icon: Briefcase, title: "Placement & Career", body: "95% placement assistance in top hospitals across India & abroad.", cta: "Placement Stats", to: "admissions" },
            { icon: Trophy, title: "Campus Life", body: "Holistic development through sports, cultural events & extracurriculars.", cta: "Life at Campus", to: "campus-engagements" },
          ].map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.title} className="bg-white rounded-2xl overflow-hidden ring-1 ring-[#E2E8F0] hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col">
                <div className="aspect-[16/9] bg-gradient-to-br from-[#0F2A5F] to-[#1B3E83] flex items-center justify-center">
                  <Icon size={48} strokeWidth={1.2} className="text-[#7FE7CE]" />
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-['Poppins',sans-serif] font-bold text-[#0F2A5F] uppercase text-sm">{c.title}</h3>
                  <p className="mt-2 text-xs text-[#475569] leading-relaxed flex-1">{c.body}</p>
                  <Link to={c.to} className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold bg-[#0FB28A] hover:bg-[#0E916F] text-white px-3 py-2 rounded-md w-fit">{c.cta}</Link>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Bottom stats */}
      <section className="py-12 bg-[#E0F5EE]">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Stat icon={GraduationCap} value="1,200+" label="Students Enrolled" big />
          <Stat icon={Users} value="150+" label="Expert Faculty" big />
          <Stat icon={Building2} value="15+" label="Associated Hospitals" big />
          <Stat icon={Trophy} value="50+" label="Awards & Recognitions" big />
        </div>
      </section>

      {/* Gallery + Enquiry */}
      <section className="py-12 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-['Poppins',sans-serif] font-bold text-2xl md:text-4xl text-[#0F2A5F]">Gallery &amp; Virtual Tour</h2>
            <div className="mt-2 h-1 w-20 bg-[#0FB28A] mx-auto rounded-full" />
          </div>
          <div className="grid lg:grid-cols-[1fr_400px] gap-8">
            <div>
              {ytEmbed ? (
                <div className="rounded-2xl overflow-hidden shadow-xl bg-black aspect-video">
                  <iframe src={ytEmbed} title="Campus Tour" loading="lazy" allowFullScreen className="w-full h-full border-0" />
                </div>
              ) : gallery.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {gallery.slice(0, 6).map((g) => (
                    <img key={g.id} src={g.image_url} alt={g.caption || ""} className="aspect-square object-cover rounded-xl ring-1 ring-[#E2E8F0]" loading="lazy" />
                  ))}
                </div>
              ) : inst.home_image_url ? (
                <img src={inst.home_image_url} alt={inst.name || "Campus"} className="rounded-2xl shadow-xl w-full aspect-video object-cover" />
              ) : (
                <div className="rounded-2xl bg-[#F4F9FF] aspect-video flex items-center justify-center text-[#475569] text-sm">
                  Campus photos will appear here once published.
                </div>
              )}
            </div>
            <EnquirySidebar inst={inst} departments={departments} />
          </div>
        </div>
      </section>

      {/* Updates */}
      <section className="py-12 md:py-16 bg-[#F4F9FF]" data-testid="nur-updates">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="font-['Poppins',sans-serif] font-bold text-2xl md:text-4xl text-[#0F2A5F]">Latest Updates</h2>
            <div className="mt-2 h-1 w-20 bg-[#0FB28A] mx-auto rounded-full" />
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            <InfoColumn title="Admissions" icon={Megaphone} items={admissions} testid="nur-home-admissions" empty="Admission notices will appear soon." />
            <InfoColumn title="Important Links" icon={Link2} items={links} testid="nur-home-links" empty="Links will appear once published." />
            <InfoColumn title="Notices" icon={Bell} items={notices} testid="nur-home-notices" empty="No notices have been posted yet." />
          </div>
        </div>
      </section>

      {/* Approvals strip */}
      <section className="py-10 bg-white border-t border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-[#0FB28A] mb-5">Approvals &amp; Disclosures</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5">
            {[
              { label: "INC Approved", sub: "Indian Nursing Council" },
              { label: "State Council", sub: "Approved" },
              { label: "Affiliated to University", sub: "" },
              { label: "Anti Ragging Committee", sub: "Active" },
              { label: "Grievance Redressal Cell", sub: "Active" },
              { label: "Transparency & Disclosure", sub: "" },
            ].map((b) => (
              <div key={b.label} className="flex items-start gap-3">
                <span className="shrink-0 w-10 h-10 rounded-md bg-[#E0F5EE] text-[#0FB28A] flex items-center justify-center"><ShieldCheck size={16} strokeWidth={1.5} /></span>
                <div className="min-w-0">
                  <div className="text-xs font-bold text-[#0F2A5F] leading-tight">{b.label}</div>
                  {b.sub && <div className="text-[10px] text-[#475569]">{b.sub}</div>}
                </div>
              </div>
            ))}
          </div>
          {inst.mandatory_disclosure_url && (
            <div className="mt-6 text-center">
              <a href={inst.mandatory_disclosure_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0FB28A] hover:text-[#0E916F]">View All Disclosures <ExternalLink size={12} /></a>
            </div>
          )}
        </div>
      </section>
    <RecentAcademicsWidget docs={academics} theme="emerald" testidPrefix="nur-ac" linkTo="academics" />
    <AlumniStrip items={(data.alumni || []).filter((a) => a.featured)} theme="light" testid="nur-alumni" registerHref="/nursing/alumni/register" />
    </div>
  );
}

function Hero({ slides, inst }) {
  const [idx, setIdx] = useState(0);
  const list = slides.length > 0 ? slides : null;
  useEffect(() => {
    if (!list || list.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 6000);
    return () => clearInterval(t);
  }, [list]);

  return (
    <section className="relative bg-white overflow-hidden" data-testid="nur-hero">
      <div className="absolute inset-0 -z-10" style={{
        background: "radial-gradient(ellipse at top right, rgba(15,178,138,0.10), transparent 60%), radial-gradient(ellipse at bottom left, rgba(15,42,95,0.06), transparent 60%)"
      }} />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[1.05fr_1fr] gap-8 items-center py-12 lg:py-20">
        <div>
          <div className="text-[11px] tracking-[0.4em] uppercase font-semibold text-[#0F2A5F]">
            Shape your career · Serve humanity
          </div>
          <h1 className="mt-4 font-['Poppins',sans-serif] font-extrabold text-3xl sm:text-4xl lg:text-6xl leading-[1.05] text-[#0F2A5F]">
            Become a <span className="block text-[#0FB28A]">Compassionate</span>
            <span className="block">Healthcare Professional</span>
          </h1>
          <p className="mt-5 text-[#475569] text-base max-w-xl">
            ANM · GNM · B.Sc Nursing · Post Basic B.Sc Nursing · M.Sc Nursing — graduating from MIT Nursing College in Chhatrapati Sambhajinagar.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="admissions" className="inline-flex items-center gap-2 bg-[#0F2A5F] hover:bg-[#0A1F47] text-white font-semibold px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all">
              Apply Online <ArrowRight size={16} strokeWidth={2} />
            </Link>
            {inst.mandatory_disclosure_url && (
              <a href={inst.mandatory_disclosure_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 border-2 border-[#0FB28A] text-[#0FB28A] hover:bg-[#E0F5EE] font-semibold px-6 py-3 rounded-lg transition-colors">
                <Download size={16} strokeWidth={2} /> Download Brochure
              </a>
            )}
            <Link to="campus-engagements" className="inline-flex items-center gap-2 border-2 border-[#0F2A5F] text-[#0F2A5F] hover:bg-[#F4F9FF] font-semibold px-6 py-3 rounded-lg transition-colors">
              <Activity size={16} strokeWidth={2} /> Virtual Tour
            </Link>
          </div>
        </div>
        {/* Right: CMS slider OR a styled placeholder */}
        <div className="relative">
          {list ? (
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#E2E8F0] bg-[#0A1F47]" style={{ aspectRatio: "1 / 1" }}>
              {list.map((s, i) => (
                <img key={s.id} src={s.image_url} alt={s.caption || ""}
                     className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === idx ? "opacity-100" : "opacity-0"}`} />
              ))}
              {list.length > 1 && (
                <>
                  <button onClick={() => setIdx((i) => (i - 1 + list.length) % list.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white text-[#0F2A5F] flex items-center justify-center shadow"><ChevronLeft size={16} /></button>
                  <button onClick={() => setIdx((i) => (i + 1) % list.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white text-[#0F2A5F] flex items-center justify-center shadow"><ChevronRight size={16} /></button>
                </>
              )}
            </div>
          ) : (
            <div className="rounded-2xl bg-gradient-to-br from-[#0F2A5F] to-[#0FB28A] aspect-square flex items-center justify-center shadow-2xl">
              <Heart size={120} strokeWidth={1} className="text-white/30" />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, value, label, big = false }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`shrink-0 ${big ? "w-12 h-12" : "w-11 h-11"} rounded-md bg-[#E0F5EE] text-[#0FB28A] flex items-center justify-center`}>
        <Icon size={big ? 22 : 20} strokeWidth={1.5} />
      </span>
      <div>
        <div className={`font-['Poppins',sans-serif] font-bold ${big ? "text-2xl" : "text-xl"} text-[#0F2A5F]`}>{value}</div>
        <div className="text-[11px] text-[#475569] uppercase tracking-wider">{label}</div>
      </div>
    </div>
  );
}

function EnquirySidebar({ inst, departments }) {
  const [form, setForm] = useState({ candidate_name: "", mobile: "", email: "", department_id: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const mobile = form.mobile.replace(/\D/g, "");
    if (!form.candidate_name.trim()) return;
    if (mobile.length !== 10) return;
    setBusy(true);
    try {
      const api = (await import("@/lib/api")).default;
      const { toast } = await import("sonner");
      await api.post(`/public/admission-inquiry/${inst.website_slug}`, {
        candidate_name: form.candidate_name.trim(), mobile, email: form.email.trim(),
        department_id: form.department_id || "", inquiry_details: "",
      });
      toast.success("Inquiry submitted! Our team will reach out shortly.");
      setForm({ candidate_name: "", mobile: "", email: "", department_id: "" });
    } catch (err) {
      const { toast } = await import("sonner");
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setBusy(false); }
  };
  const inputCls = "w-full border border-white/30 bg-white/10 placeholder-white/60 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#7FE7CE] focus:border-[#7FE7CE]";
  return (
    <aside className="bg-gradient-to-br from-[#0F2A5F] to-[#0A1F47] rounded-2xl p-6 shadow-xl text-white" data-testid="nur-home-enquiry">
      <h3 className="font-['Poppins',sans-serif] font-bold text-xl">Enquire / Apply Now</h3>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <input type="text" required maxLength={100} placeholder="Full Name" value={form.candidate_name} onChange={(e) => set("candidate_name", e.target.value)} className={inputCls} data-testid="nur-home-enquiry-name" />
        <div className="grid grid-cols-2 gap-3">
          <input type="tel" required inputMode="numeric" pattern="[0-9]{10}" maxLength={10} placeholder="Mobile Number" value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/\D/g, ""))} className={inputCls} data-testid="nur-home-enquiry-mobile" />
          <input type="email" placeholder="Email Address" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="nur-home-enquiry-email" />
        </div>
        <select value={form.department_id} onChange={(e) => set("department_id", e.target.value)} className={inputCls + " text-white"} data-testid="nur-home-enquiry-course">
          <option value="" className="text-[#0F2A5F]">Select Course</option>
          {departments.map((d) => (<option key={d.id} value={d.id} className="text-[#0F2A5F]">{d.name}</option>))}
        </select>
        <button type="submit" disabled={busy} className="w-full bg-[#0FB28A] hover:bg-[#0E916F] disabled:opacity-60 text-white font-semibold py-2.5 rounded-md transition-colors" data-testid="nur-home-enquiry-submit">
          {busy ? "Submitting…" : "Submit Enquiry"}
        </button>
      </form>
    </aside>
  );
}

function InfoColumn({ title, icon: Icon, items, empty, testid }) {
  const list = (items || []).slice(0, 8);
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const isNew = (it) => Number.isFinite(new Date(it.created_at).getTime()) && new Date(it.created_at).getTime() >= sevenDaysAgo;
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm flex flex-col overflow-hidden">
      <div className="bg-gradient-to-r from-[#0F2A5F] to-[#1B3E83] text-white px-5 py-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center"><Icon size={18} strokeWidth={1.5} /></div>
        <div className="font-['Poppins',sans-serif] font-bold text-lg">{title}</div>
      </div>
      <div className="p-4 flex-1">
        {list.length === 0 ? (
          <div className="text-sm text-[#475569] text-center py-8">{empty}</div>
        ) : (
          <ul className="divide-y divide-[#E2E8F0]">
            {list.map((it) => (
              <li key={it.id} className="py-3 flex items-start gap-3" data-testid={`${testid}-item-${it.id}`}>
                <FileText size={14} className="text-[#0FB28A] mt-0.5 shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0F2A5F] line-clamp-2 font-medium flex items-start gap-1.5">
                    <span className="flex-1">{it.name}</span>
                    {isNew(it) && <span className="shrink-0 inline-flex items-center text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-[#0FB28A] text-white animate-pulse">New</span>}
                  </div>
                  {(it.pdf_url || it.external_url || it.file_url) && (
                    <a href={it.pdf_url || it.external_url || it.file_url} target="_blank" rel="noopener noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-xs text-[#0FB28A] hover:text-[#0E916F] font-semibold">Open <ExternalLink size={10} strokeWidth={1.5} /></a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>    </div>
  );
}
